import { useEffect, useState } from 'react'
import { Container, Row, Col, Spinner, Alert, Button, Badge, Card, Breadcrumb } from 'react-bootstrap'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { AnimationOnScroll } from 'react-animation-on-scroll'
import { Helmet } from 'react-helmet-async'
import http from '../../helpers/Request'
import EventRegistrationForm from '../../components/EventRegistrationForm'
import moment from 'moment'
import { formatDateTime } from '../../helpers/BlogText'

const EventDetail = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showRegistrationForm, setShowRegistrationForm] = useState(false)

  const fetchEvent = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await http.get(`/api/events/${slug}`)
      
      if (response.data.status) {
        setEvent(response.data.data)
      } else {
        setError('Event not found')
      }
    } catch (err) {
      console.error('Error fetching event:', err)
      if (err.response?.status === 404) {
        setError('Event not found')
      } else {
        setError('Failed to load event details')
      }
    } finally {
      setLoading(false)
    }
  }

  const getCountdownText = (deadline) => {
    const now = moment()
    const deadlineDate = moment(deadline)
    const duration = moment.duration(deadlineDate.diff(now))
    
    if (duration.asMilliseconds() <= 0) {
      return 'Registration Closed'
    }
    
    const days = Math.floor(duration.asDays())
    const hours = duration.hours()
    const minutes = duration.minutes()
    
    if (days > 0) {
      return `${days} days, ${hours} hours left to register`
    } else if (hours > 0) {
      return `${hours} hours, ${minutes} minutes left to register`
    } else {
      return `${minutes} minutes left to register`
    }
  }

  const isRegistrationClosed = (deadline) => {
    return moment().isAfter(moment(deadline))
  }

  const getEventTypeBadge = (type) => {
    return type === 'online' ? (
      <Badge bg="primary" className="me-2">
        <i className="fas fa-video me-1"></i> Online Event
      </Badge>
    ) : (
      <Badge bg="success" className="me-2">
        <i className="fas fa-map-marker-alt me-1"></i> Offline Event
      </Badge>
    )
  }

  const getAvailabilityText = (event) => {
    if (event.max_participants === null) {
      return 'Unlimited slots available'
    }
    
    const available = event.available_slots
    if (available <= 0) {
      return 'Fully Booked'
    }
    
    return `${available} of ${event.max_participants} slots available`
  }

  const isFullyBooked = (event) => {
    return event.max_participants !== null && event.available_slots <= 0
  }

  const handleRegisterClick = () => {
    setShowRegistrationForm(true)
  }

  const handleRegistrationSuccess = () => {
    setShowRegistrationForm(false)
    // Refresh event data to update available slots
    fetchEvent()
  }

  const handleRegistrationCancel = () => {
    setShowRegistrationForm(false)
  }

  useEffect(() => {
    if (slug) {
      fetchEvent()
    }
  }, [slug])

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading event details...</p>
        </div>
      </Container>
    )
  }

  if (error) {
    return (
      <Container className="py-5">
        <div className="text-center py-5">
          <Alert variant="danger">
            {error}
          </Alert>
          <Button variant="outline-primary" onClick={() => navigate('/events')}>
            Back to Events
          </Button>
        </div>
      </Container>
    )
  }

  if (!event) {
    return (
      <Container className="py-5">
        <div className="text-center py-5">
          <h4>Event not found</h4>
          <p className="text-muted">The event you're looking for doesn't exist or has been removed.</p>
          <Button variant="outline-primary" onClick={() => navigate('/events')}>
            Back to Events
          </Button>
        </div>
      </Container>
    )
  }

  return (
    <>
      <Helmet>
        <title>{event.title} - SiberWander Events</title>
        <meta name="description" content={event.description ? event.description.replace(/<[^>]*>/g, '').substring(0, 160) : `Join ${event.title} - an exclusive cybersecurity event by SiberWander.`} />
        <meta name="keywords" content={`${event.title}, cybersecurity event, SiberWander, ${event.event_type} event`} />
      </Helmet>

      <div className='wrap-hidden'>
        {/* Event Details */}
        <Container className='py-5'>
          <Row className='mb-4'>
            <Col xs={12}>
              <Breadcrumb>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
                  Home
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/events" }}>
                  Events
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{event.title}</Breadcrumb.Item>
              </Breadcrumb>
            </Col>
          </Row>
          <Row className='g-4'>
            {/* Event Content */}
            <Col lg={8} className="mb-5">
              <AnimationOnScroll animateOnce={true} animateIn="animate__fadeIn" duration={.8}>
                {/* Event Image */}
                {event.image && (
                  <div className="mb-4">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="img-fluid rounded"
                      style={{ width: '100%', height: '300px', objectFit: 'cover' }}
                      onError={(e) => {e.target.style.display = 'none'}}
                    />
                  </div>
                )}

                {/* Event Header */}
                <div className="mb-4">
                  <div className="mb-3">
                    {getEventTypeBadge(event.event_type)}
                    <Badge 
                      bg={isRegistrationClosed(event.registration_deadline) ? 'danger' : 'warning'} 
                      text="dark"
                    >
                      <i className="fas fa-clock me-1"></i>
                      {getCountdownText(event.registration_deadline)}
                    </Badge>
                  </div>
                  
                  <h1 className="h2 mb-3 fw-bold">{event.title}</h1>
                  
                  {/* Event Meta Information */}
                  <div className="event-meta mb-4">
                    <Row>
                      <Col md={6} className="mb-2">
                        <i className="fas fa-calendar-alt text-brand"></i>
                        <strong>Event Date:</strong><br/>
                        <span>{formatDateTime(event.event_date)}</span>
                      </Col>
                      <Col md={6} className="mb-2">
                        <i className="fas fa-clock text-brand"></i>
                        <strong>Registration Deadline:</strong><br/>
                        <span>{formatDateTime(event.registration_deadline)}</span>
                      </Col>
                      
                      {event.event_type === 'offline' && event.event_location && (
                        <Col md={6} className="mb-2">
                          <i className="fas fa-map-marker-alt text-brand"></i>
                          <strong>Location:</strong><br/>
                          <span>{event.event_location}</span>
                        </Col>
                      )}
                      
                      <Col md={6} className="mb-2">
                        <i className="fas fa-users text-brand"></i>
                        <strong>Availability:</strong><br/>
                        <span>{getAvailabilityText(event)}</span>
                      </Col>
                    </Row>
                  </div>
                </div>

                {/* Event Description */}
                {event.description && (
                  <div className="event-description">
                    <h5 className="mb-3">About This Event</h5>
                    <div 
                      className="content-output"
                      dangerouslySetInnerHTML={{ __html: event.description }}
                    />
                  </div>
                )}
              </AnimationOnScroll>
            </Col>

            {/* Registration Sidebar */}
            <Col lg={4}>
              <AnimationOnScroll animateOnce={true} animateIn="animate__slideInRight" duration={.8}>
                <Card className="card-shadow">
                  <Card.Body>
                    {showRegistrationForm ? (
                      <EventRegistrationForm 
                        event={event}
                        onSuccess={handleRegistrationSuccess}
                        onCancel={handleRegistrationCancel}
                      />
                    ) : (
                      <>
                        <h5 className="mb-3">Register for This Event</h5>
                        
                        {/* Quick Event Info */}
                        <div className="mb-3 p-3 bg-light rounded">
                          <div className="mb-2 d-flex flex-row gap-3">
                            <strong>📅</strong><strong>{formatDateTime(event.event_date)}</strong>
                          </div>
                          
                          {event.event_type === 'offline' && event.event_location && (
                            <div className="mb-2 d-flex flex-row gap-3">
                              <strong>📍</strong><strong>{event.event_location}</strong>
                            </div>
                          )}
                          
                          {event.event_type === 'online' && (
                            <div className="mb-2 d-flex flex-row gap-3">
                              <strong>💻</strong><strong>Online Event</strong>
                            </div>
                          )}
                          
                          <div className="mb-2 d-flex flex-row gap-3">
                            <strong>👥</strong><strong>{getAvailabilityText(event)}</strong>
                          </div>
                          
                          <div className="d-flex flex-row gap-3">
                            <strong>⏰</strong><strong>{getCountdownText(event.registration_deadline)}</strong>
                          </div>
                        </div>

                        {/* Registration Button */}
                        <div className="d-grid">
                          <Button 
                            variant="brand"
                            onClick={handleRegisterClick}
                            disabled={isRegistrationClosed(event.registration_deadline) || isFullyBooked(event)}
                          >
                            {isRegistrationClosed(event.registration_deadline) 
                              ? 'Registration Closed' 
                              : isFullyBooked(event)
                              ? 'Fully Booked'
                              : 'Register Now'
                            }
                          </Button>
                        </div>

                        {/* Additional Info */}
                        <div className="mt-3 text-center">
                          <small className="text-muted">
                            Registration is free and includes email confirmation
                          </small>
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </AnimationOnScroll>
            </Col>
          </Row>
        </Container>

        {/* Related/Other Events Section */}
        <div className='bg-light py-5'>
          <Container>
            <Row>
              <Col xs={12}>
                <AnimationOnScroll animateOnce={true} animateIn="animate__fadeIn" duration={.8} animatePreScroll={true} offset={100}>
                  <div className='text-center mb-4'>
                    <h3>Other Upcoming Events</h3>
                    <p className='text-muted'>Discover more cybersecurity events and workshops</p>
                  </div>
                  
                  <div className="text-center">
                    <Button variant="outline-primary" onClick={() => navigate('/events')}>
                      View All Events
                    </Button>
                  </div>
                </AnimationOnScroll>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </>
  )
}

export default EventDetail